import { useMemo, useState } from 'react';
import { api, meetingsApi, type MeetingInviteResult } from '../api/client';
import { useAuth } from '../lib/auth';
import { copyText, inviteText, type InviteMeeting } from '../lib/meetingInvite';
import { useData } from '../lib/useData';
import { Button, Dialog, Notice } from './ui';

/**
 * Sharing a meeting (Joey, Nov 3 2026), on its page, in the Meetings list and in the room:
 * - Copy invite: the meeting's name, when (in your time zone, named), the link and how joining
 *   works, as text to paste into an email or a chat. Shown to copy by hand when the browser won't.
 * - Invite people: coworkers on your account, chosen by name. Each gets a message from you with the
 *   details, and while it's going on now, a ring like a Patch.
 */

export function CopyInviteButton({ m, company, size, variant }: { m: InviteMeeting; company?: string; size?: 'small'; variant?: 'secondary' | 'ghost' }) {
  const { state } = useAuth();
  const [copied, setCopied] = useState(false);
  const [byHand, setByHand] = useState<string | null>(null);
  const text = () => inviteText(m, { host: state?.user?.name || 'Someone', ...(company ? { company } : {}) });
  return (
    <>
      <Button
        size={size}
        variant={variant ?? 'secondary'}
        icon={copied ? 'check' : 'copy'}
        onClick={async () => {
          const t = text();
          if (await copyText(t)) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          } else setByHand(t);
        }}
      >
        {copied ? 'Invite copied' : 'Copy invite'}
      </Button>
      <Dialog
        open={byHand !== null}
        onClose={() => setByHand(null)}
        title="Copy the invite"
        subtitle="This browser wouldn’t copy it: select it and copy it by hand."
        foot={
          <Button variant="primary" onClick={() => setByHand(null)}>
            Done
          </Button>
        }
      >
        <textarea className="input textarea mono" rows={12} readOnly value={byHand ?? ''} aria-label="The invite" onFocus={(e) => e.currentTarget.select()} />
      </Dialog>
    </>
  );
}

export function InvitePeopleButton({ domain, meetingId, live, size, onInvited }: { domain: string; meetingId: string; live: boolean; size?: 'small'; onInvited?: (r: MeetingInviteResult) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} icon="users" onClick={() => setOpen(true)}>
        Invite people
      </Button>
      {open && <InvitePeopleDialog domain={domain} meetingId={meetingId} live={live} onClose={() => setOpen(false)} {...(onInvited ? { onInvited } : {})} />}
    </>
  );
}

function InvitePeopleDialog({ domain, meetingId, live, onClose, onInvited }: { domain: string; meetingId: string; live: boolean; onClose: () => void; onInvited?: (r: MeetingInviteResult) => void }) {
  const people = useData(() => api.messagePeople(domain), [domain], { live: false });
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<MeetingInviteResult | null>(null);
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (people.data?.people ?? []).filter((p) => !p.me && (!q || p.name.toLowerCase().includes(q) || (p.extension ?? '').includes(q))).sort((a, b) => a.name.localeCompare(b.name));
  }, [people.data, query]);
  const toggle = (id: string) =>
    setChosen((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await meetingsApi.invite(domain, meetingId, [...chosen]);
      setDone(r);
      onInvited?.(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t send.');
    } finally {
      setBusy(false);
    }
  };
  const names = (xs: { name: string }[]) => (xs.length <= 3 ? xs.map((x) => x.name).join(', ') : `${xs.slice(0, 3).map((x) => x.name).join(', ')} and ${xs.length - 3} more`);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite people"
      subtitle={live ? 'They get a message from you with the link, and their Stitch rings, like a Patch.' : 'They get a message from you with when it is and the link.'}
      foot={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={busy || chosen.size === 0} onClick={() => void send()}>
              {busy ? 'Inviting…' : chosen.size ? `Invite ${chosen.size}` : 'Invite'}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="section-stack">
          {done.invited.length > 0 && (
            <Notice tone="ok">
              Invited {names(done.invited)}.{done.rang ? ' Their Stitch is ringing now.' : ' They have a message from you with the details.'}
            </Notice>
          )}
          {done.skipped.map((s) => (
            <Notice key={s.id} tone="warn">
              {s.name} {s.why}.
            </Notice>
          ))}
        </div>
      ) : (
        <div className="section-stack">
          <input className="input" type="search" placeholder="Find someone" aria-label="Find someone" value={query} autoFocus onChange={(e) => setQuery(e.target.value)} />
          {people.error && <Notice tone="crit">Your coworkers didn’t load. Close this and try again.</Notice>}
          <ul className="plain-list meeting-invite-list" aria-label="Your coworkers">
            {list.map((p) => (
              <li key={p.id}>
                <label className="check-row">
                  <input type="checkbox" checked={chosen.has(p.id)} onChange={() => toggle(p.id)} />
                  <span>
                    {p.name}
                    {p.extension && <span className="muted"> · {p.extension}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {people.data && list.length === 0 && <p className="muted">{query ? 'No one matches.' : 'Nobody else in your company signs in to Stitch yet.'}</p>}
          {error && <Notice tone="crit">{error}</Notice>}
        </div>
      )}
    </Dialog>
  );
}
